/**
 * The bits chooser (registration-wallets D1): one whole-number input primed
 * with the node's mirror of the Worker's bounds, labelled with the euro
 * equivalent once the Worker's rate is known — the ONLY money the client ever
 * renders (§7, D21); the fee, the tax and the total are Stripe's page to draw.
 *
 * One component for the storefront and both registration wizards, so a member
 * registering directly gets exactly the choice a voucher buyer gets. (Found
 * live 2026-09-14: pay-and-continue silently took the default.) Bits are
 * mandatory on every checkout, so the field is never optional — a waived node
 * removes it instead (D9).
 */
import { createElement } from '@catalog-shell';
import { validateBits, bitsEuroLabel, type BitsBounds } from '../auth/voucher-mint';

export interface BitsFieldOptions {
    /** The row's label, e.g. "Bits on your new wallet". */
    label: string;
    /** Sentence after the figure: "100 000 bits ≈ €20 — <hint>". Empty = the figure alone. */
    hint: string;
    /** Initial bounds; `setBounds` re-primes when the config arrives. */
    bounds?: BitsBounds;
    /** Initial rate, when already known. */
    bitsPerEuro?: number | null;
}

/** What the member chose, or the problem to show instead. */
export type BitsChoice = { bits: number; problem: null } | { bits: null; problem: string };

export interface BitsField {
    row: HTMLElement;
    input: HTMLInputElement;
    /** Re-prime min, max and the default — the node config arrives after render. */
    setBounds(bounds: BitsBounds): void;
    /** The Worker's rate; `null` labels the figure in bits alone. */
    setRate(bitsPerEuro: number | null): void;
    read(): BitsChoice;
    /** Take the row out of the page (a waived node sells nothing). */
    remove(): void;
}

const FALLBACK_BOUNDS: BitsBounds = { min: 100_000, max: 10_000_000, default: 100_000 };

export function createBitsField(opts: BitsFieldOptions): BitsField {
    let bounds = opts.bounds ?? FALLBACK_BOUNDS;
    let rate: number | null = opts.bitsPerEuro ?? null;

    const row = createElement('div', { className: 'cid-auth-field' });
    row.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: opts.label }));
    const input = createElement('input', { className: 'cid-auth-input' });
    input.type = 'number';
    input.step = '1';
    row.appendChild(input);
    const note = createElement('p', { className: 'cid-pane-note' });
    row.appendChild(note);

    function sync(): void {
        const figure = bitsEuroLabel(Number(input.value) || 0, rate);
        note.textContent = opts.hint ? `${figure} — ${opts.hint}` : figure;
    }
    function prime(): void {
        input.min = String(bounds.min);
        input.max = String(bounds.max);
        input.value = String(bounds.default);
        sync();
    }
    input.addEventListener('input', sync);
    prime();

    return {
        row,
        input,
        setBounds(next) { bounds = next; prime(); },
        setRate(next) { rate = next; sync(); },
        read() {
            const raw = input.value.trim();
            const value = raw === '' ? Number.NaN : Number(raw);
            const problem = validateBits(value, bounds);
            return problem ? { bits: null, problem } : { bits: value, problem: null };
        },
        remove() { row.remove(); },
    };
}
